import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClientConfig } from '../models/client-config.model';

/**
 * Loads the Angular app configuration from GET /api/Configuration/sunbucks (same-origin).
 *
 * Key design decisions:
 * - Uses HttpBackend directly so the JwtInterceptor (which reads apiBaseUrl from
 *   this service) never intercepts the config request — no circular dependency.
 * - Relative URL /api/Configuration/sunbucks works because .NET hosts Angular on the same origin.
 * - Registered via APP_INITIALIZER so apiBaseUrl is guaranteed to be available
 *   before any sun-bucks service or interceptor runs.
 * - No credentials are stored or returned here.  Token acquisition is handled
 *   entirely by the BFF token proxy (POST /api/Configuration/sunbucks/token)
 *   where the X-Client-Id is injected server-side and never exposed to the browser.
 */
@Injectable({ providedIn: 'root' })
export class SunBucksConfigService {

    private config: ClientConfig | null = null;
    private readonly http: HttpClient;

    constructor(handler: HttpBackend) {
        this.http = new HttpClient(handler);
    }

    /**
     * Returns true only when load() has completed AND the critical properties
     * are populated.  Returns false if load() has not run yet, or if the
     * response did not contain a valid apiBaseUrl value.
     */
    get isLoaded(): boolean {
        return !!this.config?.apiBaseUrl;
    }

    load(): Promise<void> {
        return firstValueFrom(
            this.http.get<ClientConfig>('/api/Configuration/sunbucks')
        ).then(result => {
            this.config = result;
        }).catch(error => {
            // Log but do not re-throw — a config failure on non-SunBucks pages
            // must not crash the whole app.  The interceptors check isLoaded
            // before acting, so SunBucks calls will simply pass through until
            // the user navigates to the SunBucks module.
            console.warn('SunBucksConfigService: failed to load config', error);
        });
    }

    get apiBaseUrl(): string {
        if (!this.config?.apiBaseUrl) {
            throw new Error('apiBaseUrl not available — config not loaded yet');
        }
        return this.config.apiBaseUrl;
    }
}
